// Recovery-blob AEAD primitive. The fallback (this file) is loaded when
// the bundler can't resolve a `.web.ts` or `.macos.ts` platform variant
// — e.g. plain `tsc` and the vitest jsdom environment. The web variant
// uses `crypto.subtle`; the macOS variant uses pure-JS `@noble/*`.
//
// Envelope shape (versioned):
//
//   {
//     v: 1,
//     kdf: 'PBKDF2-SHA256',
//     iter: 600000,
//     salt: base64url(16 bytes),
//     alg: 'AES-256-GCM',
//     iv:  base64url(12 bytes),
//     ct:  base64url(ciphertext || auth_tag)
//   }
//
// JSON-serialized then base64url-encoded as the wire `ciphertext`. Format
// is owned by us (the BE only stores opaque bytes) so we control rotation;
// future versions bump `v` and the decrypt path branches on it.

export interface RecoveryAead {
  encrypt(plaintext: Uint8Array, passphrase: string): Promise<string>;
  decrypt(ciphertext: string, passphrase: string): Promise<Uint8Array>;
}

// Fallback that throws on use. Hosts that need real crypto MUST be
// resolved to a platform variant; pulling this in directly is a bug.
export const recoveryAead: RecoveryAead = {
  encrypt: () => {
    throw new Error(
      'recoveryAead: no platform implementation. Import from a host with platform extensions resolved.',
    );
  },
  decrypt: () => {
    throw new Error(
      'recoveryAead: no platform implementation. Import from a host with platform extensions resolved.',
    );
  },
};

// --- shared envelope codec, used by both platform variants ----------------

export interface RecoveryEnvelopeV1 {
  v: 1;
  kdf: 'PBKDF2-SHA256';
  iter: number;
  salt: string;
  alg: 'AES-256-GCM';
  iv: string;
  ct: string;
}

export const PBKDF2_ITER = 600_000;
export const SALT_LEN = 16;
export const IV_LEN = 12;
export const MIN_PASSPHRASE_LEN = 8;

export function bytesToUrlBase64(bytes: Uint8Array): string {
  const B = (globalThis as { Buffer?: { from(b: Uint8Array): { toString(enc: string): string } } }).Buffer;
  if (B !== undefined) {
    return B.from(bytes).toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function urlBase64ToBytes(s: string): Uint8Array {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/');
  const fill = (4 - (padded.length % 4)) % 4;
  const b64 = padded + '='.repeat(fill);
  const B = (globalThis as { Buffer?: { from(s: string, enc: string): Uint8Array } }).Buffer;
  if (B !== undefined) {
    const buf = B.from(b64, 'base64');
    return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  }
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function packEnvelope(env: RecoveryEnvelopeV1): string {
  const json = new TextEncoder().encode(JSON.stringify(env));
  return bytesToUrlBase64(json);
}

export function unpackEnvelope(ciphertext: string): RecoveryEnvelopeV1 {
  const json = new TextDecoder().decode(urlBase64ToBytes(ciphertext));
  const env = JSON.parse(json) as Partial<RecoveryEnvelopeV1>;
  if (env.v !== 1 || env.kdf !== 'PBKDF2-SHA256' || env.alg !== 'AES-256-GCM') {
    throw new Error('unsupported recovery envelope');
  }
  if (!env.salt || !env.iv || !env.ct || !env.iter) {
    throw new Error('malformed recovery envelope');
  }
  return env as RecoveryEnvelopeV1;
}
